
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useId } from "react";
import { EnhancedSignupForm } from "./EnhancedSignupForm";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";
import { cn } from "@/lib/utils";

type ViewType = 'sign_in' | 'sign_up' | 'magic_link' | 'forgotten_password' | 'update_password';

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isResettingPassword: boolean;
  isProcessingReset: boolean;
  email: string;
  setEmail: (email: string) => void;
  newPassword: string;
  setNewPassword: (password: string) => void;
  onPasswordReset: (e: React.FormEvent) => Promise<void>;
  defaultView?: ViewType;
  prefilledEmail?: string;
  presentation?: 'dialog' | 'page';
}

export function AuthDialog({
  open,
  onOpenChange,
  isResettingPassword,
  isProcessingReset,
  email,
  setEmail,
  newPassword,
  setNewPassword,
  onPasswordReset,
  defaultView = 'sign_in',
  prefilledEmail,
  presentation = 'dialog'
}: AuthDialogProps) {
  const { brand, isWhiteLabel } = useBrand();
  const [currentView, setCurrentView] = useState<ViewType>(defaultView);
  const [isProcessingSignup, setIsProcessingSignup] = useState(false);
  const [contactData, setContactData] = useState<any>(null);
  const [isInvitationFlow, setIsInvitationFlow] = useState(false);
  
  // Generate unique IDs for this dialog instance
  const uniqueId = useId();
  const emailId = `email-${uniqueId}`;
  const passwordId = `password-${uniqueId}`;
  const authContainerId = `auth-container-${uniqueId}`;

  useEffect(() => {
    if (prefilledEmail && prefilledEmail !== email) {
      setEmail(prefilledEmail);
    }
    if (defaultView !== currentView) {
      setCurrentView(defaultView);
    }

    // Check if this is an invitation flow and load contact data
    const checkInvitationFlow = async () => {
      const contactId = sessionStorage.getItem("link_contact_id");
      const pendingInvitation = sessionStorage.getItem("pendingInvitation");
      
      if ((contactId || pendingInvitation) && defaultView === 'sign_up') {
        setIsInvitationFlow(true);
        
        // Handle proposal invitation flow
        if (pendingInvitation) {
          try {
            const invitation = JSON.parse(pendingInvitation);
            console.log("📧 AuthDialog: Found proposal invitation data:", invitation);
            setContactData({
              first_name: invitation.firstName,
              last_name: invitation.lastName,
              phone: invitation.phone,
              job_title: invitation.jobTitle
            });
            return; // Exit early since we have the data
          } catch (error) {
            console.error("Error parsing proposal invitation data:", error);
          }
        }
        
        // Handle regular contact invitation flow
        if (contactId) {
          try {
            const { data: contacts, error } = await supabase
              .from('profiles')
              .select('first_name, last_name, phone, job_title')
              .eq('id', contactId)
              .limit(1);
            
            if (error) {
              console.error("Error fetching contact data:", error);
            } else if (contacts && contacts.length > 0) {
              setContactData(contacts[0]);
            }
          } catch (error) {
            console.error("Error fetching contact data:", error);
          }
        }
      }
    };

    checkInvitationFlow();
  }, [prefilledEmail, defaultView, email, setEmail, currentView]);

  // Enforce sign-in view unless an invitation is detected
  useEffect(() => {
    if (!isInvitationFlow && currentView === 'sign_up') {
      setCurrentView('sign_in');
    }
  }, [isInvitationFlow, currentView]);

  const handleSignupSuccess = () => {
    onOpenChange(false);
  };

  const getAuthConfig = () => ({
    supabaseClient: supabase,
    appearance: {
      theme: ThemeSupa,
      variables: {
        default: {
          colors: {
            brand: isWhiteLabel ? 'hsl(var(--brand-action))' : '#DFEF97',
            brandAccent: isWhiteLabel ? 'hsl(var(--brand-action-hover))' : '#D1E586'
          }
        }
      },
      className: {
        container: 'w-full',
        button: 'w-full text-black hover:opacity-90 transition-opacity',
        input: 'rounded-md',
        divider: 'my-4',
        message: 'text-center text-sm'
      }
    },
    providers: [] as any[],
    redirectTo: `${window.location.origin}/`,
    magicLink: true,
    showLinks: !isInvitationFlow,
    view: isResettingPassword ? 'update_password' : currentView,
    onlyThirdPartyProviders: false,
    localization: {
      variables: {
        sign_in: {
          email_label: 'Email address',
          password_label: 'Password',
          button_label: 'Sign in',
          loading_button_label: 'Signing in...',
          link_text: ''
        },
        sign_up: {
          email_label: 'Email address',
          password_label: 'Password',
          button_label: 'Sign up',
          loading_button_label: 'Signing up...',
          link_text: ''
        },
        forgotten_password: {
          link_text: 'Forgot your password?',
          button_label: 'Send reset instructions',
          loading_button_label: 'Sending reset instructions...',
          confirmation_text: 'Check your email for the password reset link'
        },
        update_password: {
          button_label: 'Update password',
          loading_button_label: 'Updating password...',
          password_label: 'New password'
        }
      }
    }
  });

  const title = (text: string) => presentation === 'page'
    ? <h1 className="text-center text-2xl font-semibold">{text}</h1>
    : <DialogTitle>{text}</DialogTitle>;
  const description = (text: string) => presentation === 'page'
    ? <p className="text-center text-sm text-muted-foreground">{text}</p>
    : <DialogDescription>{text}</DialogDescription>;

  const content = (
    <>
      {isResettingPassword ? (
        <>
          {title('Reset Your Password')}
          {description('Please enter your email and new password to complete the reset process.')}
          <form onSubmit={onPasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={emailId}>Email</Label>
              <Input
                id={emailId}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isProcessingReset}
                placeholder="Enter your email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={passwordId}>New Password</Label>
              <Input
                id={passwordId}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isProcessingReset}
                placeholder="Enter your new password"
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isProcessingReset}>
              {isProcessingReset ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
        </>
      ) : (
        <>
          {title(currentView === 'sign_up' 
              ? (isInvitationFlow ? 'Complete Your Invitation' : 'Create Your Account')
              : 'Welcome Back')}
          {description(currentView === 'sign_up' 
              ? (isInvitationFlow 
                  ? 'Please complete your profile to accept the invitation and create your account.'
                  : 'Create an account to continue.'
                )
              : `Sign in to ${brand?.config.company_name || 'your account'} to continue.`
            )}
          <div className="grid gap-6">
            <div className="flex flex-col items-center gap-4">
              <img 
                src={brand?.config.logo_url || "/lovable-uploads/06766b50-132e-4c3a-959f-75ffe32b144c.png"}
                alt={`${brand?.config.company_name || 'Snapshot Live'} Logo`}
                className={isWhiteLabel ? "brand-auth-logo max-w-full object-contain" : "h-24 max-w-full object-contain"}
                style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined}
              />
            </div>
            
            {currentView === 'sign_up' && isInvitationFlow ? (
              <EnhancedSignupForm
                email={email}
                prefilledData={{
                  firstName: contactData?.first_name || '',
                  lastName: contactData?.last_name || '',
                  phone: contactData?.phone || '',
                  jobTitle: contactData?.job_title || ''
                }}
                onSuccess={handleSignupSuccess}
                isProcessing={isProcessingSignup}
                setIsProcessing={setIsProcessingSignup}
              />
            ) : (
              <div id={authContainerId}>
                <Auth {...getAuthConfig()} />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  if (presentation === 'page') {
    return <section className={cn("brand-login-panel w-full max-w-md", isResettingPassword && "space-y-4")}>{content}</section>;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {content}
      </DialogContent>
    </Dialog>
  );
}
